/**
 * +world-resources: deterministic placement of breakables, hidden caches and reagent nodes from a
 * dungeon's layout (room bounds, corridors, pack spawns, boss arenas, the route). Placement is
 * authored data (the same every run); what each object DROPS is rolled per run by the sim from
 * the run seed.
 *
 * Rules (docs/design/world-resources.md §3, art bible §4.3 / §6.1 "packs to keep clear"):
 *  - every object stands on the walkable floor of its room, inset from the dressing ledge;
 *  - nothing within PACK_CLEAR of a trash pack's centre, nor the entrance spawn;
 *  - boss rooms: nothing in the central 70% of the arena or inside the boss's clear radius;
 *  - corridor mouths stay clear (corridor strip + CORRIDOR_CLEAR yd);
 *  - objects never overlap each other;
 *  - objects are not obstacles: they never block movement or pathing whatever their position.
 */
import type { DungeonDef, PackDef, Rect, Vec2, WorldObjectDef, WorldObjectKind } from '@keystone/sim';
import { createSeededRng } from '@keystone/sim';
import { WORLD_OBJECT_KINDS } from './loot';

export const PACK_CLEAR = 9;
export const ENTRANCE_CLEAR = 8;
export const CORRIDOR_CLEAR = 3;
/** Edge clusters stand this far inside the walkable edge (the ledge lip). */
export const EDGE_INSET = 1.6;
/** Middle clutter keeps this far from the walls. */
export const MIDDLE_INSET = 7;
/** Boss arenas: the central fraction of the room that stays clear. */
export const BOSS_CENTRAL = 0.7;
/** Middle clutter keeps this far off the route's walk line. */
export const ROUTE_LINE_CLEAR = 2.5;
/** Zone tags by distance to the route polyline. */
export const ZONE_ROUTE = 10;
export const ZONE_SIDE = 25;

export type BreakableKind = Extract<WorldObjectKind, 'urn' | 'crate' | 'bone_pile' | 'egg_clutch' | 'cocoon' | 'offering_bowl'>;

export interface RoomWorldSpec {
  roomId: string;
  /** Breakables to place. */
  breakables: number;
  /** Kind weights for clusters. */
  kinds: Partial<Record<BreakableKind, number>>;
  /** Share of the budget placed in the room middle (forced to 0 in boss rooms). */
  middle: number;
  /** Hidden caches (placed in the corners farthest from the route). */
  caches?: number;
  /** Reagent nodes, each at the nearest valid point to `near`. */
  nodes?: { kind: 'venom_gland' | 'eye_shard'; near: Vec2 }[];
}

export interface BossClear {
  roomId: string;
  center: Vec2;
  radius: number;
}

const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);
const r2 = (v: number): number => Math.round(v * 100) / 100;

export function rectOf(points: readonly Vec2[]): Rect {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

export const packCentre = (p: PackDef): Vec2 => ({ x: p.spawns.reduce((s, q) => s + q.pos.x, 0) / p.spawns.length, y: p.spawns.reduce((s, q) => s + q.pos.y, 0) / p.spawns.length });

function segDist(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  const t = l2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0;
  return Math.hypot(p.x - (a.x + dx * t), p.y - (a.y + dy * t));
}

/** Corridor centreline (a → b) and width from its strip polygon (geometry.stripPoly order). */
export function corridorAxis(points: readonly Vec2[]): { a: Vec2; b: Vec2; width: number } {
  const [p0, p1, p2, p3] = points as [Vec2, Vec2, Vec2, Vec2];
  const mid = (u: Vec2, v: Vec2): Vec2 => ({ x: (u.x + v.x) / 2, y: (u.y + v.y) / 2 });
  // The short sides are p0–p3 and p1–p2.
  const sideA = dist(p0, p3);
  const sideB = dist(p0, p1);
  return sideA <= sideB ? { a: mid(p0, p3), b: mid(p1, p2), width: sideA } : { a: mid(p0, p1), b: mid(p3, p2), width: sideB };
}

/**
 * The walked route as a polyline: entrance → each route pack's centre, threading the corridors
 * between rooms (BFS over the corridor graph).
 */
export function routePolyline(d: DungeonDef): Vec2[] {
  const pts: Vec2[] = [d.entrance];
  const packById = new Map(d.packs.map((p) => [p.id, p]));
  const roomOfEntrance = d.rooms.find((r) => {
    const b = rectOf(r.shape.points);
    return d.entrance.x >= b.x && d.entrance.x <= b.x + b.w && d.entrance.y >= b.y && d.entrance.y <= b.y + b.h;
  })?.id;
  let room = roomOfEntrance ?? d.packs[0]?.roomId ?? '';
  for (const id of d.route) {
    const p = packById.get(id);
    if (!p) continue;
    if (p.roomId !== room) {
      // BFS for the corridor chain room → p.roomId.
      const prev = new Map<string, { from: string; corridor: number }>();
      const q = [room];
      const seen = new Set([room]);
      while (q.length) {
        const r = q.shift()!;
        if (r === p.roomId) break;
        d.corridors.forEach((c, i) => {
          const other = c.from === r ? c.to : c.to === r ? c.from : null;
          if (other && !seen.has(other)) {
            seen.add(other);
            prev.set(other, { from: r, corridor: i });
            q.push(other);
          }
        });
      }
      const chain: { corridor: number; forward: boolean }[] = [];
      for (let r = p.roomId; prev.has(r); r = prev.get(r)!.from) {
        const s = prev.get(r)!;
        chain.unshift({ corridor: s.corridor, forward: d.corridors[s.corridor]!.from === s.from });
      }
      for (const link of chain) {
        const ax = corridorAxis(d.corridors[link.corridor]!.shape.points);
        pts.push(...(link.forward ? [ax.a, ax.b] : [ax.b, ax.a]));
      }
      room = p.roomId;
    }
    pts.push(packCentre(p));
  }
  return pts;
}

export function distanceToPolyline(p: Vec2, line: readonly Vec2[]): number {
  let best = Infinity;
  for (let i = 1; i < line.length; i++) best = Math.min(best, segDist(p, line[i - 1]!, line[i]!));
  return line.length === 1 ? dist(p, line[0]!) : best;
}

/** Why a point can't hold a world object of radius `r` in `roomId` (null = fine). */
export function placementProblem(d: DungeonDef, bosses: readonly BossClear[], roomId: string, p: Vec2, r: number): string | null {
  const room = d.rooms.find((x) => x.id === roomId);
  if (!room) return 'no room';
  const b = rectOf(room.shape.points);
  const m = r + 0.5;
  if (p.x < b.x + m || p.x > b.x + b.w - m || p.y < b.y + m || p.y > b.y + b.h - m) return 'off the walkable floor';
  if (dist(p, d.entrance) < ENTRANCE_CLEAR + r) return 'entrance spawn';
  for (const pk of d.packs) {
    if (pk.encounterId) continue;
    if (dist(p, packCentre(pk)) < PACK_CLEAR + r) return `pack ${pk.id}`;
    for (const s of pk.spawns) if (dist(p, s.pos) < 4 + r) return `pack ${pk.id} spawn`;
  }
  for (const bc of bosses) {
    if (dist(p, bc.center) < bc.radius + r) return `boss clear ${bc.roomId}`;
    if (bc.roomId === roomId) {
      const ix = (b.w * (1 - BOSS_CENTRAL)) / 2;
      const iy = (b.h * (1 - BOSS_CENTRAL)) / 2;
      if (p.x > b.x + ix && p.x < b.x + b.w - ix && p.y > b.y + iy && p.y < b.y + b.h - iy) return `boss arena centre ${roomId}`;
    }
  }
  for (const c of d.corridors) {
    const ax = corridorAxis(c.shape.points);
    if (segDist(p, ax.a, ax.b) < ax.width / 2 + CORRIDOR_CLEAR + r) return `corridor ${c.id}`;
  }
  // Pillars / fountains: keep a body's width clear around every obstacle.
  if ((d.obstacles ?? []).some((ob) => rectContains(rectOf(ob.points), p, r + 0.8))) return 'obstacle';
  return null;
}

/** Every rule violation of a dungeon's authored world objects (empty = valid). */
export function worldPlacementProblems(d: DungeonDef, bosses: readonly BossClear[]): string[] {
  const out: string[] = [];
  const objs = d.worldObjects ?? [];
  const ids = new Set<string>();
  objs.forEach((o, i) => {
    if (ids.has(o.id)) out.push(`${o.id}: duplicate id`);
    ids.add(o.id);
    const why = placementProblem(d, bosses, o.roomId, o.pos, o.radius);
    if (why) out.push(`${o.id}: ${why}`);
    for (let j = 0; j < i; j++) {
      const q = objs[j]!;
      if (dist(o.pos, q.pos) < o.radius + q.radius + 0.3) out.push(`${o.id}: overlaps ${q.id}`);
    }
    if ((d.obstacles ?? []).some((ob) => rectContains(rectOf(ob.points), o.pos, o.radius))) out.push(`${o.id}: inside an obstacle`);
  });
  return out;
}

const rectContains = (b: Rect, p: Vec2, r: number): boolean => p.x > b.x - r && p.x < b.x + b.w + r && p.y > b.y - r && p.y < b.y + b.h + r;

function weightedPick<T extends string>(w: Partial<Record<T, number>>, u: number): T {
  const entries = Object.entries(w) as [T, number][];
  const total = entries.reduce((s, [, v]) => s + v, 0);
  let x = u * total;
  for (const [k, v] of entries) {
    x -= v;
    if (x <= 0) return k;
  }
  return entries[entries.length - 1]![0];
}

/** Place the world objects of a dungeon (pure, deterministic for a given `seed`). */
export function placeWorldObjects(d: DungeonDef, specs: readonly RoomWorldSpec[], bosses: readonly BossClear[], seed = 1): WorldObjectDef[] {
  const rng = createSeededRng(seed);
  const route = routePolyline(d);
  const out: WorldObjectDef[] = [];
  const zoneOf = (p: Vec2): WorldObjectDef['zone'] => {
    const dd = distanceToPolyline(p, route);
    return dd <= ZONE_ROUTE ? 'route' : dd <= ZONE_SIDE ? 'side' : 'off';
  };
  const fits = (roomId: string, p: Vec2, r: number): boolean =>
    placementProblem(d, bosses, roomId, p, r) === null && out.every((o) => dist(o.pos, p) >= o.radius + r + 0.4);
  const add = (roomId: string, kind: WorldObjectKind, p: Vec2, extra: Partial<WorldObjectDef> = {}): WorldObjectDef => {
    const k = WORLD_OBJECT_KINDS[kind];
    const pos = { x: r2(p.x), y: r2(p.y) };
    const o: WorldObjectDef = {
      id: `wo_${roomId}_${String(out.filter((x) => x.roomId === roomId).length + 1).padStart(2, '0')}`,
      kind,
      class: k.class,
      pos,
      facing: r2(rng.next() * Math.PI * 2),
      roomId,
      hp: k.hp,
      radius: k.radius,
      loot: k.loot,
      zone: zoneOf(pos),
      variant: r2(rng.next()),
      ...extra,
    };
    out.push(o);
    return o;
  };

  for (const spec of specs) {
    const room = d.rooms.find((r) => r.id === spec.roomId);
    if (!room) continue;
    const b = rectOf(room.shape.points);
    const isBoss = bosses.some((x) => x.roomId === spec.roomId);

    // Nodes first (authored spots), then caches (corners far from the route), then clutter.
    for (const n of spec.nodes ?? []) {
      const r = WORLD_OBJECT_KINDS[n.kind].radius;
      const p = nearestFit(n.near, (q) => fits(spec.roomId, q, r));
      if (p) add(spec.roomId, n.kind, p);
    }
    const cacheR = WORLD_OBJECT_KINDS.hidden_cache.radius;
    const corners: Vec2[] = [];
    for (const fx of [0, 0.25, 0.5, 0.75, 1])
      for (const fy of [0, 0.25, 0.5, 0.75, 1]) {
        if (fx !== 0 && fx !== 1 && fy !== 0 && fy !== 1) continue; // perimeter only
        corners.push({ x: b.x + 2.5 + fx * (b.w - 5), y: b.y + 2.5 + fy * (b.h - 5) });
      }
    corners.sort((p, q) => distanceToPolyline(q, route) - distanceToPolyline(p, route) || p.x - q.x || p.y - q.y);
    let caches = 0;
    for (const c of corners) {
      if (caches >= (spec.caches ?? 0)) break;
      if (out.some((o) => o.class === 'cache' && dist(o.pos, c) < 25)) continue;
      const p = nearestFit(c, (q) => fits(spec.roomId, q, cacheR), 6);
      if (!p) continue;
      add(spec.roomId, 'hidden_cache', p, { hidden: true });
      caches++;
    }

    // Clutter candidates: the edge band and (trash rooms) the middle.
    const edge: { p: Vec2; t: Vec2 }[] = [];
    const step = 1.2;
    const x0 = b.x + EDGE_INSET;
    const x1 = b.x + b.w - EDGE_INSET;
    const y0 = b.y + EDGE_INSET;
    const y1 = b.y + b.h - EDGE_INSET;
    for (let x = x0; x <= x1; x += step) {
      edge.push({ p: { x, y: y0 }, t: { x: 1, y: 0 } });
      edge.push({ p: { x, y: y1 }, t: { x: 1, y: 0 } });
    }
    for (let y = y0 + step; y < y1; y += step) {
      edge.push({ p: { x: x0, y }, t: { x: 0, y: 1 } });
      edge.push({ p: { x: x1, y }, t: { x: 0, y: 1 } });
    }
    const middle: { p: Vec2; t: Vec2 }[] = [];
    if (!isBoss)
      for (let x = b.x + MIDDLE_INSET; x <= b.x + b.w - MIDDLE_INSET; x += 2.5)
        for (let y = b.y + MIDDLE_INSET; y <= b.y + b.h - MIDDLE_INSET; y += 2.5)
          if (distanceToPolyline({ x, y }, route) >= ROUTE_LINE_CLEAR + 1) middle.push({ p: { x, y }, t: { x: 1, y: 0 } });
    shuffle(edge, rng);
    shuffle(middle, rng);
    const midBudget = isBoss ? 0 : Math.round(spec.breakables * spec.middle);
    let placed = 0;
    const anchors: Vec2[] = [];
    const fill = (cands: { p: Vec2; t: Vec2 }[], budget: number, spacing: number): void => {
      let n = 0;
      for (const c of cands) {
        if (n >= budget || placed >= spec.breakables) return;
        if (anchors.some((a) => dist(a, c.p) < spacing)) continue;
        const kind = weightedPick(spec.kinds, rng.next()) as BreakableKind;
        const size = Math.min(budget - n, 1 + Math.floor(rng.next() * 3));
        const offs = [0, 1.35, -1.35];
        let got = 0;
        for (let i = 0; i < size; i++) {
          const k2 = i > 0 && rng.next() < 0.3 ? (weightedPick(spec.kinds, rng.next()) as BreakableKind) : kind;
          const jitter = (rng.next() - 0.5) * 0.4;
          const p = { x: c.p.x + c.t.x * offs[i]! + c.t.y * jitter, y: c.p.y + c.t.y * offs[i]! + c.t.x * jitter };
          if (!fits(spec.roomId, p, WORLD_OBJECT_KINDS[k2].radius)) continue;
          add(spec.roomId, k2, p);
          got++;
        }
        if (got) {
          anchors.push(c.p);
          n += got;
          placed += got;
        }
      }
    };
    fill(middle, midBudget, 9);
    fill(edge, spec.breakables - placed, 6);
  }
  return out;
}

function nearestFit(near: Vec2, ok: (p: Vec2) => boolean, maxR = 12): Vec2 | null {
  if (ok(near)) return near;
  for (let r = 0.5; r <= maxR; r += 0.5)
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2;
      const p = { x: near.x + Math.cos(a) * r, y: near.y + Math.sin(a) * r };
      if (ok(p)) return p;
    }
  return null;
}

function shuffle<T>(a: T[], rng: { next(): number }): void {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng.next() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
}
